import React, { useState, useEffect, useRef, useCallback } from 'react';
import Sidebar from '../components/Sidebar';
import { chatAPI, digestAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  MessageSquarePlus,
  Send,
  Trash2,
  Bot,
  User,
  Loader2,
  MessageSquare,
  Sparkles,
  ChevronLeft,
  Zap,
  Mail,
  MailCheck,
  Clock,
  ToggleLeft,
  ToggleRight,
} from 'lucide-react';

function MarkdownText({ text }) {
  const lines = text.split('\n');
  const elements = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Code blocks
    if (line.startsWith('```')) {
      const codeLines = [];
      i++;
      while (i < lines.length && !lines[i].startsWith('```')) {
        codeLines.push(lines[i]);
        i++;
      }
      i++; // skip closing ```
      elements.push(
        <pre key={`code-${i}`} className="bg-black/20 rounded-lg p-3 my-2 overflow-x-auto text-sm font-mono">
          <code>{codeLines.join('\n')}</code>
        </pre>
      );
      continue;
    }

    // Headers
    if (line.startsWith('### ')) {
      elements.push(<h4 key={i} className="font-semibold text-base mt-3 mb-1">{line.slice(4)}</h4>);
    } else if (line.startsWith('## ')) {
      elements.push(<h3 key={i} className="font-semibold text-lg mt-3 mb-1">{line.slice(3)}</h3>);
    } else if (line.startsWith('# ')) {
      elements.push(<h2 key={i} className="font-bold text-xl mt-3 mb-1">{line.slice(2)}</h2>);
    }
    // List items
    else if (line.match(/^[-*]\s/)) {
      elements.push(
        <li key={i} className="ml-4 list-disc">
          <InlineFormat text={line.slice(2)} />
        </li>
      );
    } else if (line.match(/^\d+\.\s/)) {
      const content = line.replace(/^\d+\.\s/, '');
      elements.push(
        <li key={i} className="ml-4 list-decimal">
          <InlineFormat text={content} />
        </li>
      );
    }
    // Empty line
    else if (line.trim() === '') {
      elements.push(<div key={i} className="h-2" />);
    }
    // Regular paragraph
    else {
      elements.push(
        <p key={i} className="leading-relaxed">
          <InlineFormat text={line} />
        </p>
      );
    }
    i++;
  }

  return <div className="space-y-0.5">{elements}</div>;
}

function InlineFormat({ text }) {
  // Bold + inline code
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
  return parts.map((part, idx) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={idx} className="font-semibold">{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith('`') && part.endsWith('`')) {
      return <code key={idx} className="bg-black/15 px-1.5 py-0.5 rounded text-sm font-mono">{part.slice(1, -1)}</code>;
    }
    return <span key={idx}>{part}</span>;
  });
}

export default function AIAssistantPage() {
  const { isAdmin, isSuperAdmin } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [activeSession, setActiveSession] = useState(null);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [showSessions, setShowSessions] = useState(true);
  const [digestEnabled, setDigestEnabled] = useState(false);
  const [digestLoading, setDigestLoading] = useState(false);
  const [digestHistory, setDigestHistory] = useState([]);
  const [sendingDigest, setSendingDigest] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    fetchSessions();
    fetchDigestSettings();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [activeSession?.messages, scrollToBottom]);

  useEffect(() => {
    if (activeSessionId) {
      fetchSession(activeSessionId);
    } else {
      setActiveSession(null);
    }
  }, [activeSessionId]);

  const fetchSessions = async () => {
    try {
      const res = await chatAPI.listSessions();
      setSessions(res.data);
    } catch {
      toast.error('Failed to load chat sessions');
    } finally {
      setLoadingSessions(false);
    }
  };

  const fetchDigestSettings = async () => {
    try {
      const [settingsRes, historyRes] = await Promise.all([
        digestAPI.getSettings(),
        digestAPI.getHistory()
      ]);
      setDigestEnabled(settingsRes.data.enabled || false);
      setDigestHistory(historyRes.data || []);
    } catch { /* silent */ }
  };

  const toggleDigest = async () => {
    setDigestLoading(true);
    try {
      const res = await digestAPI.updateSettings({ enabled: !digestEnabled });
      setDigestEnabled(res.data.enabled);
      toast.success(res.data.enabled ? 'Weekly digest enabled' : 'Weekly digest disabled');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || 'Failed to update digest settings');
    } finally {
      setDigestLoading(false);
    }
  };

  const sendDigestNow = async () => {
    setSendingDigest(true);
    try {
      await digestAPI.sendNow();
      toast.success('Digest sent to your email!');
      fetchDigestSettings();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || 'Failed to send digest');
    } finally {
      setSendingDigest(false);
    }
  };

  const fetchSession = async (sessionId) => {
    try {
      const res = await chatAPI.getSession(sessionId);
      setActiveSession(res.data);
    } catch {
      toast.error('Failed to load conversation');
    }
  };

  const createSession = async (withRecommendations = false) => {
    try {
      const res = await chatAPI.createSession({ title: 'New Conversation' });
      setSessions(prev => [res.data, ...prev]);
      setActiveSessionId(res.data.id);
      setActiveSession(res.data);
      setShowSessions(false);
      
      if (withRecommendations) {
        setSending(true);
        try {
          const recoRes = await chatAPI.getRecommendations(res.data.id);
          setActiveSession(prev => ({
            ...prev,
            title: 'Case Recommendations',
            messages: [...(prev?.messages || []), recoRes.data.ai_message]
          }));
          setSessions(prev => prev.map(s =>
            s.id === res.data.id ? { ...s, title: 'Case Recommendations' } : s
          ));
        } catch (err) {
          const detail = err?.response?.data?.detail;
          toast.error(detail || 'Failed to generate recommendations');
        } finally {
          setSending(false);
        }
      } else {
        setTimeout(() => inputRef.current?.focus(), 100);
      }
    } catch {
      toast.error('Failed to create conversation');
    }
  };

  const deleteSession = async (e, sessionId) => {
    e.stopPropagation();
    try {
      await chatAPI.deleteSession(sessionId);
      setSessions(prev => prev.filter(s => s.id !== sessionId));
      if (activeSessionId === sessionId) {
        setActiveSessionId(null);
        setActiveSession(null);
      }
      toast.success('Conversation deleted');
    } catch {
      toast.error('Failed to delete conversation');
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!message.trim() || sending || !activeSessionId) return;

    const userMsg = message.trim();
    setMessage('');
    setSending(true);

    // Optimistic update
    const tempUserMsg = { role: 'user', content: userMsg, timestamp: new Date().toISOString() };
    setActiveSession(prev => ({
      ...prev,
      messages: [...(prev?.messages || []), tempUserMsg]
    }));

    try {
      const res = await chatAPI.sendMessage(activeSessionId, { message: userMsg });
      // Replace optimistic with real data + add AI response
      setActiveSession(prev => ({
        ...prev,
        messages: [
          ...(prev?.messages || []).slice(0, -1),
          res.data.user_message,
          res.data.ai_message
        ]
      }));
      // Update session title if changed
      if (res.data.session_title) {
        setSessions(prev => prev.map(s =>
          s.id === activeSessionId ? { ...s, title: res.data.session_title } : s
        ));
      }
    } catch (err) {
      // Remove optimistic message
      setActiveSession(prev => ({
        ...prev,
        messages: (prev?.messages || []).slice(0, -1)
      }));
      const detail = err?.response?.data?.detail;
      toast.error(detail || 'Failed to send message. Please try again.');
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const messages = activeSession?.messages || [];

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar />
      <main className="flex-1 md:ml-64 flex flex-col h-screen" data-testid="ai-assistant-page">
        <div className="flex flex-1 overflow-hidden">
          {/* Sessions Panel */}
          <div className={`${showSessions && !activeSessionId ? 'flex' : 'hidden'} md:flex flex-col w-full md:w-72 lg:w-80 border-r border-border bg-card shrink-0`}>
            <div className="p-4 border-b border-border">
              <Button
                onClick={createSession}
                className="w-full gap-2"
                data-testid="new-chat-btn"
              >
                <MessageSquarePlus className="w-4 h-4" />
                New Conversation
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto p-2 space-y-1" data-testid="chat-sessions-list">
              {loadingSessions ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                </div>
              ) : sessions.length === 0 ? (
                <div className="text-center py-8 px-4">
                  <MessageSquare className="w-8 h-8 mx-auto text-muted-foreground/40 mb-2" />
                  <p className="text-sm text-muted-foreground">No conversations yet</p>
                </div>
              ) : (
                sessions.map(s => (
                  <div
                    key={s.id}
                    onClick={() => { setActiveSessionId(s.id); setShowSessions(false); }}
                    className={`w-full text-left px-3 py-2.5 rounded-lg transition-colors group flex items-center gap-2 cursor-pointer ${
                      activeSessionId === s.id
                        ? 'bg-primary/10 text-primary'
                        : 'hover:bg-secondary text-foreground'
                    }`}
                    data-testid={`chat-session-${s.id}`}
                  >
                    <MessageSquare className="w-4 h-4 shrink-0 opacity-50" />
                    <span className="flex-1 truncate text-sm">{s.title}</span>
                    <button
                      onClick={(e) => deleteSession(e, s.id)}
                      className="opacity-0 group-hover:opacity-100 p-1 hover:bg-destructive/10 rounded transition-opacity"
                      data-testid={`delete-session-${s.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5 text-destructive" />
                    </button>
                  </div>
                ))
              )}
            </div>

            {/* Weekly Digest Panel */}
            {(isAdmin || isSuperAdmin) && (
              <div className="border-t border-border p-3 space-y-2" data-testid="digest-panel">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    <span className="text-xs font-medium text-foreground">Weekly Digest</span>
                  </div>
                  <button
                    onClick={toggleDigest}
                    disabled={digestLoading}
                    className="text-muted-foreground hover:text-foreground transition-colors"
                    data-testid="digest-toggle-btn"
                    title={digestEnabled ? 'Disable weekly digest' : 'Enable weekly digest'}
                  >
                    {digestEnabled ? (
                      <ToggleRight className="w-6 h-6 text-primary" />
                    ) : (
                      <ToggleLeft className="w-6 h-6" />
                    )}
                  </button>
                </div>
                {digestEnabled && (
                  <div className="space-y-2">
                    <p className="text-[11px] text-muted-foreground flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Sent every Monday at 7 AM UTC
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full text-xs gap-1.5"
                      onClick={sendDigestNow}
                      disabled={sendingDigest}
                      data-testid="send-digest-now-btn"
                    >
                      {sendingDigest ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <MailCheck className="w-3 h-3" />
                      )}
                      {sendingDigest ? 'Generating...' : 'Send Digest Now'}
                    </Button>
                    {digestHistory.length > 0 && (
                      <p className="text-[10px] text-muted-foreground/60">
                        Last sent: {new Date(digestHistory[0].sent_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Chat Area */}
          <div className={`${!showSessions || activeSessionId ? 'flex' : 'hidden'} md:flex flex-col flex-1 min-w-0`}>
            {activeSessionId ? (
              <>
                {/* Chat header */}
                <div className="flex items-center gap-3 px-4 md:px-6 py-3 border-b border-border bg-card">
                  <button
                    onClick={() => { setShowSessions(true); setActiveSessionId(null); }}
                    className="md:hidden p-1 rounded hover:bg-secondary"
                    data-testid="back-to-sessions-btn"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <Sparkles className="w-4 h-4 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-sm font-semibold text-foreground truncate max-w-[200px] md:max-w-md">
                        {activeSession?.title || 'Loading...'}
                      </h2>
                      <p className="text-xs text-muted-foreground">GPT-5.2 Immigration Assistant</p>
                    </div>
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto px-4 md:px-6 py-4 space-y-4" data-testid="chat-messages">
                  {messages.length === 0 && !sending && (
                    <div className="flex flex-col items-center justify-center h-full text-center px-4">
                      <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
                        <Bot className="w-8 h-8 text-primary" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground mb-2">NewSlate AI Assistant</h3>
                      <p className="text-sm text-muted-foreground max-w-md">
                        Ask me about immigration processes, case management, form requirements, 
                        or any questions about your organization's cases.
                      </p>

                      {/* Case Insights CTA */}
                      <Button
                        onClick={async () => {
                          setSending(true);
                          try {
                            const res = await chatAPI.getRecommendations(activeSessionId);
                            setActiveSession(prev => ({
                              ...prev,
                              title: 'Case Recommendations',
                              messages: [...(prev?.messages || []), res.data.ai_message]
                            }));
                            setSessions(prev => prev.map(s =>
                              s.id === activeSessionId ? { ...s, title: 'Case Recommendations' } : s
                            ));
                          } catch {
                            toast.error('Failed to generate case insights');
                          } finally {
                            setSending(false);
                          }
                        }}
                        variant="outline"
                        className="mt-5 gap-2 border-amber-500/30 text-amber-600 dark:text-amber-400 hover:bg-amber-500/10"
                        data-testid="case-insights-btn"
                      >
                        <Zap className="w-4 h-4" />
                        Analyze My Cases &amp; Get Recommendations
                      </Button>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-5 max-w-lg w-full">
                        {[
                          'What are the steps for an N-400 application?',
                          'Show me a summary of my active cases',
                          'What documents are needed for a work visa?',
                          'How long does green card processing take?',
                        ].map((q, idx) => (
                          <button
                            key={idx}
                            onClick={() => { setMessage(q); inputRef.current?.focus(); }}
                            className="text-left text-xs px-3 py-2.5 rounded-lg border border-border hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground"
                            data-testid={`suggestion-${idx}`}
                          >
                            {q}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {messages.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                      data-testid={`message-${idx}`}
                    >
                      {msg.role === 'assistant' && (
                        <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-1">
                          <Bot className="w-4 h-4 text-primary" />
                        </div>
                      )}
                      <div
                        className={`max-w-[80%] md:max-w-[70%] rounded-2xl px-4 py-3 text-sm ${
                          msg.role === 'user'
                            ? 'bg-primary text-primary-foreground rounded-br-md'
                            : 'bg-secondary text-secondary-foreground rounded-bl-md'
                        }`}
                      >
                        {msg.role === 'assistant' ? (
                          <MarkdownText text={msg.content} />
                        ) : (
                          <p className="whitespace-pre-wrap">{msg.content}</p>
                        )}
                      </div>
                      {msg.role === 'user' && (
                        <div className="w-7 h-7 rounded-full bg-foreground/10 flex items-center justify-center shrink-0 mt-1">
                          <User className="w-4 h-4 text-foreground/70" />
                        </div>
                      )}
                    </div>
                  ))}

                  {sending && (
                    <div className="flex gap-3" data-testid="typing-indicator">
                      <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                        <Bot className="w-4 h-4 text-primary" />
                      </div>
                      <div className="bg-secondary rounded-2xl rounded-bl-md px-4 py-3">
                        <div className="flex gap-1.5">
                          <span className="w-2 h-2 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: '0ms' }} />
                          <span className="w-2 h-2 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: '150ms' }} />
                          <span className="w-2 h-2 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                <div className="border-t border-border bg-card px-4 md:px-6 py-3">
                  <form onSubmit={sendMessage} className="flex items-end gap-2">
                    <textarea
                      ref={inputRef}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          sendMessage(e);
                        }
                      }}
                      placeholder="Ask about immigration, cases, or troubleshooting..."
                      className="flex-1 resize-none bg-secondary border border-border rounded-xl px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 max-h-32 min-h-[42px]"
                      rows={1}
                      disabled={sending}
                      data-testid="chat-input"
                    />
                    <Button
                      type="submit"
                      size="icon"
                      disabled={!message.trim() || sending}
                      className="rounded-xl h-[42px] w-[42px] shrink-0"
                      data-testid="send-message-btn"
                    >
                      {sending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                    </Button>
                  </form>
                  <p className="text-[10px] text-muted-foreground/50 mt-1.5 text-center">
                    AI assistant for informational purposes. Not legal advice.
                  </p>
                </div>
              </>
            ) : (
              /* Empty state for desktop when no session selected */
              <div className="hidden md:flex flex-col items-center justify-center h-full text-center px-4">
                <div className="w-20 h-20 rounded-2xl bg-primary/5 flex items-center justify-center mb-6">
                  <Sparkles className="w-10 h-10 text-primary/40" />
                </div>
                <h2 className="text-xl font-semibold text-foreground mb-2">AI Immigration Assistant</h2>
                <p className="text-sm text-muted-foreground max-w-sm mb-6">
                  Get instant answers about immigration processes, your cases, and CRM workflows.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button onClick={() => createSession(false)} className="gap-2" data-testid="new-chat-empty-btn">
                    <MessageSquarePlus className="w-4 h-4" />
                    Start a Conversation
                  </Button>
                  <Button
                    onClick={() => createSession(true)}
                    variant="outline"
                    className="gap-2 border-amber-500/30 text-amber-600 dark:text-amber-400 hover:bg-amber-500/10"
                    data-testid="case-insights-empty-btn"
                  >
                    <Zap className="w-4 h-4" />
                    Get Case Insights
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
